import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User } from '../types';
import { authAPI } from '../services/api';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (setFreshLogin?: boolean) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const ALLOWED_EMAIL_DOMAIN = 'ottonomy.io';
const DISALLOWED_DOMAIN_MESSAGE = `Only @${ALLOWED_EMAIL_DOMAIN} accounts can access this dashboard.`;

const assertAllowedUser = (candidate: User): User => {
  const normalizedEmail = String(candidate.email || '').trim().toLowerCase();
  if (!normalizedEmail.endsWith(`@${ALLOWED_EMAIL_DOMAIN}`)) {
    throw new Error(DISALLOWED_DOMAIN_MESSAGE);
  }

  return candidate;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const bootstrapSession = async () => {
      try {
        const response = await authAPI.getMe();
        if (mounted && response.success && response.data) {
          setUser(assertAllowedUser(response.data));
        }
      } catch (error) {
        if (mounted) {
          setUser(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    const handleSessionExpired = () => {
      if (mounted) {
        setUser(null);
        sessionStorage.removeItem('freshLogin');
      }
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    bootstrapSession();

    return () => {
      mounted = false;
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, []);

  const login = async (_email: string, _password: string) => {
    throw new Error('Local authentication is not supported. Use WorkOS SSO.');
  };

  const loginWithToken = useCallback(async (setFreshLogin: boolean = false) => {
    try {
      const response = await authAPI.getMe();

      if (response.success && response.data) {
        setUser(assertAllowedUser(response.data));
        // Only set freshLogin flag if explicitly requested (from callback)
        if (setFreshLogin) {
          sessionStorage.setItem('freshLogin', 'true');
        }
      } else {
        throw new Error('Failed to get user profile');
      }
    } catch (error) {
      console.error('AuthContext: WorkOS login error:', error);
      setUser(null);
      throw error;
    }
  }, []);

  const register = async (_name: string, _email: string, _password: string) => {
    throw new Error('Local registration is not supported. Use WorkOS SSO.');
  };

  const logout = () => {
    setUser(null);
    sessionStorage.removeItem('freshLogin');

    authAPI.logout()
      .then((response) => {
        const logoutUrl = response.data?.logoutUrl;
        if (logoutUrl) {
          window.location.href = logoutUrl;
        }
      })
      .catch(() => undefined);
  };

  const value = {
    user,
    login,
    loginWithToken,
    register,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
